import { getCurrentInstance, nextTick } from 'vue'
import { useStore } from '@/store/Expedientes/pacientes.js'

export const formHandlingMixin = {
  methods: {
    // Método para manejar el envío del formulario
    async submitForm(
      proxy,
      entity,
      storeNamespace,
      actionType,
      element,
      recordName = '',
      resourceName
    ) {
      const { addPatientData, selectPatient } = useStore()

      try {
        // Formatear la fecha de nacimiento si existe y no está ya en formato ISO
        if (entity.birthdate && !entity.birthdate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          try {
            const date = new Date(entity.birthdate);
            if (!isNaN(date.getTime())) {
              entity.birthdate = date.toISOString().split('T')[0];
            }
          } catch (error) {
            console.warn('Error formateando fecha de nacimiento:', entity.birthdate, error);
          }
        }

        proxy.$store.commit(`${storeNamespace}/CLEAR_ERROR`)
        proxy.$store.commit(`${storeNamespace}/CLEAR_FIELD_ERRORS`)

        const response = await proxy.$store.dispatch(
          `${storeNamespace}/${actionType}`,
          {
            resource: resourceName,
            itemData: entity
          }
        )


        // Si es un paciente, agregarlo/actualizarlo en el store local después de guardarlo
        if (resourceName === 'pacientes' && response?.data) {
          // Usar los datos retornados por el backend que incluyen el ID generado
          const patientData = response.data
          addPatientData(patientData)
          selectPatient(patientData.id)
        } else {
          console.log('FormHandlingMixin - NOT adding to store. resourceName:', resourceName, 'response.data:', response?.data)
        }

        // Verificar si la respuesta del servidor contiene un mensaje
        const successMessage =
          response?.message || this.getSuccessMessage(actionType)

        proxy.$notify(
          recordName,
          successMessage,
          'success',
          'ni ni-like-2 ni-lg'
        )
        proxy.$emit('entityUpdated')
        proxy.$emit('update:modelValue', false)

        // Asegurarse de que el DOM esté actualizado
        await nextTick()

        // Animar la actualización del ítem si el elemento está presente
        if (element) {
          this.animateUpdate(element)
        }

        return { message: successMessage, data: response?.data }
      } catch (error) {
        console.error('Error en submitForm:', error)
        const errorMessage = this.getErrorMessage(error)
        proxy.$store.commit(`${storeNamespace}/SET_ERROR`, errorMessage)

        // Manejar errores de validación (422)
        if (error.response?.status === 422) {
          proxy.$store.commit(`${storeNamespace}/SET_FIELD_ERRORS`, error.response.data.errors || {})
          proxy.$notify(
            'Error de validación',
            errorMessage,
            'danger',
            'ni ni-fat-remove ni-lg'
          )
        } else {
          proxy.$notify(
            'Error',
            errorMessage,
            'danger',
            'ni ni-fat-remove ni-lg'
          )
        }

        return false
      }
    },

    async deleteEntity(id, recordName, storeNamespace, resourceName) {
      const { removePatientId } = useStore()

      try {
        const result = await this.$swal({
          title: '¿Estás seguro?',
          html: `¡No podrás revertir esto!<br>¿Deseas eliminar, <b>${recordName}</b>?`,
          icon: 'warning',
          showCancelButton: true,
          cancelButtonText: 'Cancelar',
          confirmButtonText: 'Sí, ¡elimínalo!',
          customClass: {
            confirmButton: 'btn bg-gradient-success',
            cancelButton: 'btn bg-gradient-danger'
          },
          buttonsStyling: false
        })

        if (result.isConfirmed) {
          const response = await this.$store.dispatch(
            `${storeNamespace}/deleteRecord`,
            {
              resource: resourceName,
              itemId: id
            }
          )

          if (resourceName === 'pacientes' && response.deleted_patient) {
            removePatientId(response.deleted_patient.id)
            // Suponiendo que `response.data` contiene el paciente actualizado
          }

          // Verificar si la respuesta del servidor contiene un mensaje
          const successMessage = response?.message

          this.$swal({
            title: '¡Eliminado!',
            text: successMessage,
            icon: 'success',
            customClass: {
              confirmButton: 'btn bg-gradient-success'
            },
            buttonsStyling: false
          })

          // Emite un evento o ejecuta alguna acción adicional si es necesario
          this.$emit('entityDeleted')
        }
      } catch (error) {
        this.$swal({
          title: '¡Error!',
          text: `Hubo un problema al eliminar ${recordName}.`,
          icon: 'error',
          customClass: {
            confirmButton: 'btn bg-gradient-danger'
          },
          buttonsStyling: false
        })
      }
    },

    // Método para manejar el submit desde setup con instancia actual
    async handleSubmit(entity, action, storeNamespace, emit) {
      const instance = getCurrentInstance()
      if (!instance) {
        console.error(
          'getCurrentInstance() returned null. Make sure this function is called within setup().'
        )
        return
      }
      const { proxy } = instance
      try {
        if (entity.birthdate) {
          const formattedBirthdate = new Date(entity.birthdate)
            .toISOString()
            .split('T')[0]
          entity.birthdate = formattedBirthdate
        }

        proxy.$store.commit(`${storeNamespace}/CLEAR_ERROR`)
        proxy.$store.commit(`${storeNamespace}/CLEAR_FIELD_ERRORS`)

        const response = await proxy.$store.dispatch(
          `${storeNamespace}/${action}`,
          entity
        )

        // Verificar si la respuesta del servidor contiene un mensaje
        const successMessage = response?.message

        proxy.$notify(
          successMessage,
          '', // Deja el mensaje vacío ya que el título es suficiente
          'success',
          'ni ni-like-2 ni-lg'
        )
        emit('entityUpdated')
        this.closeModal(emit)

        // Asegurarse de que el DOM esté actualizado
        await nextTick()

        // Animar la actualización del ítem si el elemento está presente
        const element = document.querySelector(`[data-id="${entity.id}"]`)
        if (element) {
          this.animateUpdate(element)
        }
      } catch (error) {
        const errorMessage = this.getErrorMessage(error)
        proxy.$store.commit(`${storeNamespace}/SET_ERROR`, errorMessage)
      }
    },

    // Función para generar mensaje de error basado en la respuesta del servidor
    getErrorMessage(error) {
      if (error.response && error.response.status === 422) {
        const validationErrors = error.response.data.errors
        return Object.values(validationErrors).flat().join(', ')
      } else {
        return error.response?.data?.message || 'Error en el formulario'
      }
    },

    // Método para cerrar el modal
    closeModal(emit) {
      emit('update:modelValue', false)
    },

    // Método para resetear el formulario
    resetForm(
      defaultData,
      storeNamespace,
      isEditMode,
      entityData,
      entity,
      store
    ) {
      if (isEditMode) {
        entity.value = { ...entityData }
      } else {
        entity.value = { ...defaultData }
      }
      store.commit(`${storeNamespace}/CLEAR_ERROR`)
      store.commit(`${storeNamespace}/CLEAR_FIELD_ERRORS`)
    },

    // Función para animar la actualización de un ítem
    animateUpdate(element) {
      if (element) {
        element.classList.add('update-animation')
        setTimeout(() => {
          element.classList.remove('update-animation')
        }, 3000) // Duración de la animación en ms
      } else {
        console.error('Elemento no encontrado para animar.')
      }
    }
  }
}
